import firebase from '@/firebase'
const db = firebase.db

const state = {
  users: [],
  clients: [],
  referenceModels: [],
  mmmModels: [],
  userQueue: [],
  pendingPlans: []
}

const getters = {
  users: state => {
    return state.users
  },
  clients: state => {
    return state.clients
  },
  referenceModels: state => {
    return state.referenceModels
  },
  mmmModels: state => {
    return state.mmmModels
  },
  userQueue: state => {
    return state.userQueue
  },
  pendingPlans: state => {
    return state.pendingPlans
  }
}

const actions = {
  // The three ngtorch override actions each mirror the resulting value back into the auth module,
  // because getCurrentUser reads the user document once at login rather than listening to it - so
  // without the root commit the banner would not appear or disappear until a reload.
  loadNgtorchRefs (context) {
    return firebase.functions.httpsCallable('ngtorch-listRefs')().then(function (result) {
      return result.data
    })
  },
  setNgtorchOverride (context, payload) {
    return firebase.functions.httpsCallable('ngtorch-setOverride')({
      ref: payload.ref,
      reinstall: payload.reinstall === true
    }).then(function (result) {
      context.commit('auth/setNgtorchOverride', result.data, { root: true })
      return result.data
    })
  },
  clearNgtorchOverride (context) {
    return firebase.functions.httpsCallable('ngtorch-clearOverride')().then(function (result) {
      context.commit('auth/setNgtorchOverride', null, { root: true })
      return result.data
    })
  },
  deployModel (context, payload) {
    return new Promise((resolve, reject) => {
      firebase.functions.httpsCallable('api-exportModel')({
        targetEnv: payload.targetEnv,
        sourceCodename: payload.sourceCodename,
        targetName: payload.targetName,
        modelType: payload.type
      }).then(function (result) {
        console.log(result)
        resolve(result)
      }).catch(function (error) {
        console.error(error)
        reject(error)
      })
    })
  },
  insertMap (context, payload) {
    var data = {}
    data[payload.field] = JSON.parse(payload.json.replace(/'/g, '"'))
    // data[payload.field] = JSON.stringify(JSON.parse(payload.json.replace(/'/g, '"')))
    db.doc(payload.path).update(data).then(function (result) {
      console.log(result)
    }).catch(function (error) {
      console.log(error)
    })
  },
  loadUsers (context) {
    db.collection('users').onSnapshot(function (snapshot) {
      var users = []
      snapshot.forEach(doc => {
        users.push({
          ...doc.data(),
          id: doc.id
        })
      })
      context.commit('setUsers', {
        users: users
      })
    })
  },
  loadUserQueue (context) {
    db.collection('user_queue').onSnapshot(function (snapshot) {
      var items = []
      snapshot.forEach(doc => {
        items.push({
          ...doc.data(),
          id: doc.id
        })
      })
      context.commit('setUserQueue', {
        items: items
      })
    })
  },
  loadClients (context) {
    db.collection('clients').get().then(function (snapshot) {
      var clients = []
      snapshot.forEach(doc => {
        clients.push({
          ...doc.data(),
          id: doc.id
        })
      })
      context.commit('setClients', {
        clients: clients
      })
    })
  },
  loadReferenceModels (context) {
    db.collection('reference_models').onSnapshot(function (snapshot) {
      var models = []
      snapshot.forEach(doc => {
        models.push({
          ...doc.data(),
          id: doc.id
        })
      })
      context.commit('setReferenceModels', {
        referenceModels: models
      })
    })
  },
  loadMmmModels (context) {
    db.collection('clients').where('type', '==', 'mmm').onSnapshot(function (snapshot) {
      var models = []
      snapshot.forEach(doc => {
        models.push({
          ...doc.data(),
          id: doc.id
        })
      })
      context.commit('setMmmModels', {
        mmmModels: models
      })
    })
  },
  loadPendingPlans (context) {
    db.collection('queue').where('date_completed', '==', false).orderBy('date_created', 'desc').limit(200).onSnapshot(function (snapshot) {
      var items = []
      snapshot.forEach(doc => {
        const data = doc.data()
        if ('budget' in data && !data.date_completed) {
          items.push({
            ...data,
            id: doc.id
          })
        }
      })
      context.commit('setPendingPlans', {
        pendingPlans: items
      })
    })
  },
  createUser (context, payload) {
    db.collection('user_queue').add({
      ...payload,
      operation: 'create',
      date_created: firebase.firestore.FieldValue.serverTimestamp()
    }).then(function (docRef) {
      console.log('User creation queued with ID: ', docRef.id)
    }).catch(function (error) {
      console.error('Error queuing user creation: ', error)
    })
  },
  editUser (context, payload) {
    db.collection('users').doc(payload.id).set({
      ...payload,
      date_edited: firebase.firestore.FieldValue.serverTimestamp()
    }, { merge: true }).then(function () {
      console.log('User edited: ', payload.id)
    }).catch(function (error) {
      console.error('Error editing user: ', error)
    })
  },
  deleteUser (context, payload) {
    db.collection('user_queue').add({
      ...payload,
      operation: 'delete',
      date_created: firebase.firestore.FieldValue.serverTimestamp()
    }).then(function (docRef) {
      console.log('User deletion queued with ID: ', docRef.id)
    }).catch(function (error) {
      console.error('Error queuing user deletion: ', error)
    })
  },
  createReferenceModel (context, payload) {
    db.collection('reference_models').doc(payload.codename).set({
      codename: payload.codename,
      config: JSON.parse(payload.json.replace(/'/g, '"')),
      date_created: firebase.firestore.FieldValue.serverTimestamp(),
      created_by: payload.createdBy,
      pkl: {
        name: payload.pkl.name,
        size: payload.pkl.size
      }
    }).then(function (docRef) {
      console.log('Reference model created')
    }).catch(function (error) {
      console.error('Error creating reference model', error)
    })
  },
  createMmmModel (context, payload) {
    let newClientObj = {
      code: payload.codename,
      name: payload.name,
      type: 'mmm',
      enabledStages: payload.enabledStages,
      stages: payload.stages,
      enableDataUploader: payload.enableDataUploader,
      enableAI: payload.enableAI,
      enableImpressionsModel: payload.enableImpressionsModel,
      slackChannel: payload.slackChannel,
      reference_model: {
        codename: payload.codename,
        date_created: firebase.firestore.FieldValue.serverTimestamp(),
        created_by: payload.createdBy
      },
      date_created: firebase.firestore.FieldValue.serverTimestamp(),
      created_by: payload.createdBy
    }
    if (payload.json) {
      newClientObj.reference_model.config = JSON.parse(payload.json.replace(/'/g, '"'))
    }
    if (payload.pkl) {
      newClientObj.reference_model.pkl = {
        name: payload.pkl.name,
        size: payload.pkl.size
      }
    }
    // Left out for the default T4 and for anyone but an admin, whom firestore.rules refuse it to.
    if (payload.optimizerGpu && payload.optimizerGpu !== 't4') {
      newClientObj.optimizer_gpu = payload.optimizerGpu
    }
    if (payload.useDifferentConfigForPlans) {
      newClientObj.plan_reference_model = {
        codename: payload.codename,
        date_created: firebase.firestore.FieldValue.serverTimestamp(),
        created_by: payload.createdBy
      }
      if (payload.planJson) {
        newClientObj.plan_reference_model.config = JSON.parse(payload.planJson.replace(/'/g, '"'))
      }
      if (payload.planPkl) {
        newClientObj.plan_reference_model.pkl = {
          name: payload.planPkl.name,
          size: payload.planPkl.size
        }
      }
    }
    return db.collection('clients').doc(payload.codename).set(newClientObj).then(function (docRef) {
      console.log('Ad-Hoc model created')
    }).catch(function (error) {
      console.error('Error creating Ad-Hoc model', error)
      throw error
    })
  },
  // Links an Ad-Hoc model as the next version of a family (familyId: any of its models), updates its
  // version notes, or unlinks it (familyId: null). Linking also copies the previous version's access.
  setModelVersion (context, payload) {
    return firebase.functions.httpsCallable('modelVersions-setModelVersion')({
      modelId: payload.modelId,
      familyId: payload.familyId || null,
      notes: payload.notes || '',
      userAgent: navigator.userAgent
    }).then(function (result) {
      return result.data
    })
  },
  editReferenceModel (context, payload) {
    var obj = {
      config: JSON.parse(payload.json.replace(/'/g, '"')),
      date_edited: firebase.firestore.FieldValue.serverTimestamp(),
      edited_by: payload.editedBy,
      codename: payload.codename
    }
    if (payload.pkl) {
      obj.pkl = {
        name: payload.pkl.name,
        size: payload.pkl.size
      }
    }
    db.collection('reference_models').doc(payload.codename).update(obj).then(function (docRef) {
      console.log('Reference model updated')
    }).catch(function (error) {
      console.error('Error updating reference model', error)
    })
  },
  editMmmModel (context, payload) {
    var obj = {
      date_edited: firebase.firestore.FieldValue.serverTimestamp(),
      edited_by: payload.editedBy,
      codename: payload.codename
    }
    if (payload.json) {
      obj.config = JSON.parse(payload.json.replace(/'/g, '"'))
    }
    if (payload.pkl) {
      obj.pkl = {
        name: payload.pkl.name,
        size: payload.pkl.size
      }
    }

    let updateObj = {
      name: payload.name,
      reference_model: obj,
      enabledStages: payload.enabledStages,
      stages: payload.stages,
      enableDataUploader: payload.enableDataUploader,
      enableAI: payload.enableAI,
      enableImpressionsModel: payload.enableImpressionsModel,
      slackChannel: payload.slackChannel,
      date_edited: firebase.firestore.FieldValue.serverTimestamp(),
      edited_by: payload.editedBy
    }

    if (payload.useDifferentConfigForPlans) {
      var planObj = {
        codename: payload.codename,
        date_edited: firebase.firestore.FieldValue.serverTimestamp(),
        edited_by: payload.editedBy
      }
      if (payload.planJson) {
        planObj.config = JSON.parse(payload.planJson.replace(/'/g, '"'))
      }
      if (payload.planPkl) {
        planObj.pkl = {
          name: payload.planPkl.name,
          size: payload.planPkl.size
        }
      }
      updateObj.plan_reference_model = planObj
    } else {
      updateObj.plan_reference_model = firebase.firestore.FieldValue.delete()
    }
    // Undefined unless an admin edited the model, so nobody else's save touches the field.
    if (payload.optimizerGpu !== undefined) {
      updateObj.optimizer_gpu = payload.optimizerGpu === 't4' ? firebase.firestore.FieldValue.delete() : payload.optimizerGpu
    }

    db.collection('clients').doc(payload.codename).update(updateObj).then(function (docRef) {
      console.log('Ad-Hoc model updated')
    }).catch(function (error) {
      console.error('Error updating Ad-Hoc model', error)
    })
  },
  deleteReferenceModel (context, payload) {
    db.collection('reference_models').doc(payload.codename).delete().then(function () {
      console.log('Reference Model deleted')
    }).catch(function (error) {
      console.error('Error deleting Reference Model', error)
    })
  },
  disableReferenceModel (context, payload) {
    db.collection('reference_models').doc(payload.codename).update({
      disabled: payload.disabled
    }).then(function () {
      console.log('Reference Model enabled/disabled')
    }).catch(function (error) {
      console.error('Error enabling/disabling Reference Model', error)
    })
  },
  // Moves the model to the trash, where it can be restored until it is purged.
  deleteMmmModel (context, payload) {
    return context.dispatch('manager/deleteCustomModel', {
      client: payload.codename,
      source: 'adHocModels'
    }, { root: true })
  },
  recalculateModelReport (context, payload) {
    return new Promise((resolve, reject) => {
      var recalculateModelReport = firebase.functions.httpsCallable('recalculateModelReport')
      // Support both old style (just clientId) and new style (object with clientId and userId)
      const data = typeof payload === 'string' ? { clientId: payload } : payload
      recalculateModelReport(data).then(function (result) {
        resolve(result)
      }).catch(function (error) {
        reject(error)
      })
    })
  },
  // Preview which models match the bulk-recalculation filters (type + has-report + report date
  // range). Returns { matches, undetermined, total } from the backend. Admin-only (enforced server-side).
  previewBulkRecalculateModelReports (context, filters) {
    return new Promise((resolve, reject) => {
      var bulkRecalculate = firebase.functions.httpsCallable('bulkRecalculateModelReports')
      bulkRecalculate({
        dryRun: true,
        afterDate: filters.afterDate || null,
        beforeDate: filters.beforeDate || null,
        types: filters.types || []
      }).then(function (result) {
        resolve(result.data)
      }).catch(function (error) {
        reject(error)
      })
    })
  },
  // Trigger recalculation for a confirmed batch of model ids. The backend caps the batch size.
  bulkRecalculateModelReports (context, payload) {
    return new Promise((resolve, reject) => {
      var bulkRecalculate = firebase.functions.httpsCallable('bulkRecalculateModelReports')
      bulkRecalculate({
        dryRun: false,
        ids: payload.ids || []
      }).then(function (result) {
        resolve(result.data)
      }).catch(function (error) {
        reject(error)
      })
    })
  },
  // Read the recalculating_model_report flag for a set of model ids so the UI can tell when a
  // batch has finished (the flag is cleared by the modelReportReady trigger on completion).
  // Returns a map of id -> true when that model is still recalculating.
  getRecalculatingStatus (context, ids) {
    const idList = Array.isArray(ids) ? ids : []
    return Promise.all(idList.map(function (id) {
      return db.collection('clients').doc(id).get().then(function (snapshot) {
        return { id: id, recalculating: !!(snapshot.exists && snapshot.data().recalculating_model_report) }
      }).catch(function () {
        // On a read error, treat the model as no longer recalculating so the batch can't stall.
        return { id: id, recalculating: false }
      })
    })).then(function (results) {
      const map = {}
      results.forEach(function (r) { map[r.id] = r.recalculating })
      return map
    })
  },
  restartPendingPlan (context, item) {
    return new Promise((resolve, reject) => {
      db.collection('clients').doc(item.clientId).collection('simulations').doc(item.simulationId).get().then((snapshot) => {
        if (snapshot.exists) {
          var restartPendingPlan = firebase.functions.httpsCallable('restartPendingPlan')
          restartPendingPlan({ clientId: item.clientId, queueItemId: item.queueItemId }).then(function (result) {
            resolve(result)
          }).catch(function (error) {
            reject(error)
          })
        } else {
          reject(new Error('deleted'))
        }
      })
    })
  },
  deletePendingPlan (context, item) {
    return new Promise((resolve, reject) => {
      db.collection('clients').doc(item.clientId).collection('simulations').doc(item.simulationId).delete().then(function () {
        console.log('Simulation document successfully deleted!')
        // The queue item is deliberately left in place: it holds the only remaining copy of the
        // plan, which /export/plan/ falls back to once the simulation is gone, and this action is
        // typically used right after reading a failure report. The deletedSimulation trigger tags
        // it as completed, which is what takes it off this list, exactly as when a user deletes a
        // plan from the app.
        resolve()
      }).catch(function (error) {
        console.error('Error removing simulation document: ', error)
        reject(error)
      })
    })
  },
  updateModelStatus (context, payload) {
    return new Promise((resolve, reject) => {
      db.collection(payload.modelCollectionPath).doc(payload.modelId).update({
        model_status: payload.newStatus
      }).then(function () {
        console.log(`Model status updated to ${payload.newStatus}`)
        resolve()
      }).catch(function (error) {
        console.error(`Error updating model status for model ${payload.modelId}`, error)
        reject(error)
      })
    })
  }
}

const mutations = {
  setUsers (state, payload) {
    state.users = payload.users
  },
  setUserQueue (state, payload) {
    state.userQueue = payload.items
  },
  setClients (state, payload) {
    state.clients = payload.clients
  },
  setReferenceModels (state, payload) {
    state.referenceModels = payload.referenceModels
  },
  setMmmModels (state, payload) {
    state.mmmModels = payload.mmmModels
  },
  setPendingPlans (state, payload) {
    state.pendingPlans = payload.pendingPlans
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
